import { format, isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { Box, Tooltip } from "@mui/material";
import {
  BodyLarge,
  BodyMedium,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import {
  CaseManagementTableGroup,
  tableColumnsMappingCaseManagement,
} from "../../constants.js";

export const returnDefaultColumnConfigs = () => {
  return {
    tableGroup: CaseManagementTableGroup,
  };
};

export const returnDefaultColumnConfigsForTable = (colors) => {
  const renderCellFunctions = {
    "Case Number": (params) => {
      return (
        <BodyLarge sx={{ fontWeight: "500" }}>
          {params.value}
        </BodyLarge>
      );
    },
    caseStatus: (params) => {
      const statusColors = {
        New: colors?.legacy?.blue?.info || "#2962FF", // Info blue for new cases
        Active: colors?.primary?.main || "#2D55C9", // Primary blue for active cases
        "Need More Information": colors?.warning?.main || "#ff9d00", // Orange for needing info
        "Information Provided": colors?.legacy?.blue?.accent || "#3366ff", // Accent blue for info provided
        Escalated: colors?.error?.dark || "#C62828", // Dark red for escalated cases
        "Customer Action Required": colors?.warning?.dark || "#F57C00", // Dark orange for customer action
        "Pending Release": colors?.legacy?.blue?.light || "#00c3ff", // Light blue for pending release
        RMA: colors?.legacy?.misc?.purple || "#7d5fc9", // Purple for RMA
        "RMA In Progress": "#9C27B0", // Different purple shade for RMA in progress
        "Awaiting Customer Verification": colors?.legacy?.orange?.light || "#ffa500", // Light orange for awaiting verification
        "On Hold": colors?.secondary?.main || "#747685", // Gray for on hold
        Closed: colors?.success?.main || "#219150", // Green for closed cases
      };
      return (
        <Box
          sx={{
            backgroundColor: statusColors[params.value] || colors?.primary?.main,
            color: "white",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "0.75rem",
            fontWeight: "500",
          }}
        >
          {params.value}
        </Box>
      );
    },
    createdAt: (params) => {
      if (!params?.value) return <BodyLarge>{""}</BodyLarge>;
      const date = parseISO(params.value);
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return (
        <BodyLarge>
          {isValid(date) ? formatInTimeZone(date, timeZone, "MMM dd, yyyy hh:mm a") : params.value}
        </BodyLarge>
      );
    },
    updatedAt: (params) => {
      if (!params?.value) return <BodyLarge>{""}</BodyLarge>;
      const date = parseISO(params.value);
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return (
        <BodyLarge>
          {isValid(date) ? formatInTimeZone(date, timeZone, "MMM dd, yyyy hh:mm a") : params.value}
        </BodyLarge>
      );
    },
        serialNo: (params) => {
      const serialNo = params.value;
      if (!serialNo || serialNo.trim() === "") {
        return <BodyLarge sx={{ color: colors?.text?.muted }}>N/A</BodyLarge>;
      }
      return <BodyLarge>{serialNo}</BodyLarge>;
    },
    serialType: (params) => {
      const serialType = params.value;
      if (!serialType || serialType === "none") {
        return <BodyLarge sx={{ color: colors?.text?.muted }}>N/A</BodyLarge>;
      }

      // Capitalize first letter and format the type
      const formattedType = serialType.charAt(0).toUpperCase() + serialType.slice(1);
      return <BodyLarge>{formattedType}</BodyLarge>;
    },
    summary: (params) => {
      const summary = params.value;
      if (!summary || summary.trim() === "") {
        return <BodyLarge sx={{ color: colors?.text?.muted }}>N/A</BodyLarge>;
      }

      // Truncate summary if it's too long, but respect word boundaries
      let truncatedSummary = summary;
      if (summary.length > 50) {
        // Find the last space before 50 characters
        const lastSpaceIndex = summary.substring(0, 50).lastIndexOf(' ');
        if (lastSpaceIndex > 0) {
          // Truncate at the last complete word
          truncatedSummary = summary.substring(0, lastSpaceIndex) + '...';
        } else {
          // If no space found, truncate at 50 characters
          truncatedSummary = summary.substring(0, 50) + '...';
        }
      }

      return (
        <Tooltip
          title={summary}
          placement="top-start"
          arrow
          sx={{
            maxWidth: '400px',
            '& .MuiTooltip-tooltip': {
              fontSize: '0.875rem',
              backgroundColor: 'rgba(0, 0, 0, 0.87)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }
          }}
        >
          <BodyLarge
            sx={{
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline"
              }
            }}
          >
            {truncatedSummary}
          </BodyLarge>
        </Tooltip>
      );
    },
    firmware: (params) => {
      const firmwareVersion = params.value;
      if (!firmwareVersion || firmwareVersion.trim() === "" || firmwareVersion === "NA") {
        return <BodyLarge></BodyLarge>;
      }

      return <BodyLarge>{firmwareVersion}</BodyLarge>;
    },
  };

  return Object.entries(tableColumnsMappingCaseManagement).map(
    ([headerName, field]) => {
      return {
        field: field,
        headerName: headerName,
        minWidth: 106,
        flex: 1,
        sortable: field !== 'latestComment', // Disable sorting for latestComment
        renderCell: renderCellFunctions[field]
          ? renderCellFunctions[field]
          : (params) => {
              return <BodyLarge>{params?.value}</BodyLarge>;
            },
      };
    }
  );
};
